import {
  Button,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import { dataContext } from '..'
import { ALL_GSA_COLUMNS, GsaRow, NORMALIZED_COLUMNS } from './GsaMasCatalog.types'
import { useGsaMasCatalogData } from './useGsaMasCatalogData'
import { useGsaMasCatalogFilters } from './useGsaMasCatalogFilters'
import GsaMasCatalogFilters from './GsaMasCatalogFilters'
import EditDisplayFieldsDrawer from './EditDisplayFieldsDrawer'
import '../components/styles/ListPage.css'
import './GsaMasCatalog.css'

const DEFAULT_VISIBLE: (keyof GsaRow)[] = ALL_GSA_COLUMNS.map(c => c.key)

export default function GsaMasCatalog() {
  const { user } = useContext(dataContext)
  const theme = useTheme()
  const isWideScreen = useMediaQuery(theme.breakpoints.up('xl')) // xl = 1536px
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const { filters, setFilter, clearFilters, search, committedFilters, advancedOpen, setAdvancedOpen } =
    useGsaMasCatalogFilters()
  const { rows, total, loading } = useGsaMasCatalogData(committedFilters, page, rowsPerPage)

  const [normalizedMode, setNormalizedMode] = useState(false)
  const [visibleKeys, setVisibleKeys] = useState<(keyof GsaRow)[]>(DEFAULT_VISIBLE)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [displayTemplateName, setDisplayTemplateName] = useState('Default')
  const [savedTemplates, setSavedTemplates] = useState<{ TemplateName: string; Columns: { Name: string }[] }[]>([])
  const [saveViewOpen, setSaveViewOpen] = useState(false)
  const [viewName, setViewName] = useState('')
  const [expandedDesc, setExpandedDesc] = useState<Partial<Record<number, boolean>>>({})

  const activeColumns = normalizedMode
    ? NORMALIZED_COLUMNS
    : visibleKeys
        .map(k => ALL_GSA_COLUMNS.find(c => c.key === k))
        .filter((c): c is (typeof ALL_GSA_COLUMNS)[number] => !!c)

  // ── Lock page scroll while on this page ───────────────────────────────────
  useEffect(() => {
    document.title = 'GSA MAS Contract Catalog | Order Portal'
    document.body.style.overflow = 'hidden'
    return () => {
      document.title = ''
      document.body.style.overflow = ''
    }
  }, [])

  // ── Load the user's saved GSA MAS templates on mount ──────────────────────
  useEffect(() => {
    if (!user.email) return
    loadTemplates()
  }, [user.email])

  async function loadTemplates() {
    try {
      const res = await axios.get<{ TemplateName: string; Columns: { Name: string }[] }[]>(
        `/getTemplates/${user.email}`,
      )
      setSavedTemplates(res.data.filter(t => t.TemplateName.startsWith('gsa-mas:')))
    } catch (err) {
      console.error('Failed to load templates', err)
    }
  }

  // ── Apply a saved template's columns when the user selects one ─────────────
  function handleSelectTemplate(name: string) {
    setDisplayTemplateName(name)
    if (name === 'Default') {
      setVisibleKeys(DEFAULT_VISIBLE)
      return
    }
    const template = savedTemplates.find(t => t.TemplateName === `gsa-mas:${name}`)
    if (template) {
      const keys = template.Columns.map(c => c.Name as keyof GsaRow).filter(k =>
        ALL_GSA_COLUMNS.some(col => col.key === k),
      )
      setVisibleKeys(keys)
    }
  }

  async function saveTemplate(keys: (keyof GsaRow)[]) {
    setVisibleKeys(keys)
    if (viewName.trim()) {
      try {
        await axios.post('/saveTemplate', {
          userEmail: user.email,
          templateName: `gsa-mas:${viewName}`,
          columns: keys.map((k, i) => ({ Name: k, Width: 150, Order: i })),
        })
        setDisplayTemplateName(viewName)
        await loadTemplates() // ── Refresh dropdown after saving ──────────────
      } catch (err) {
        console.error('Failed to save display template', err)
      }
    }
  }

  async function handleDeleteTemplate() {
    try {
      await axios.delete('/deleteTemplate', {
        data: { userEmail: user.email, templateName: `gsa-mas:${displayTemplateName}` },
      })
      setVisibleKeys(DEFAULT_VISIBLE)
      setDisplayTemplateName('Default')
      await loadTemplates() // ── Refresh dropdown after deleting ────────────
    } catch (err) {
      console.error('Failed to delete template', err)
    }
  }

  return (
    <div className="list-page gsa-page">
      <GsaMasCatalogFilters
        filters={filters}
        setFilter={setFilter}
        clearFilters={() => {
          clearFilters()
          setPage(0)
        }}
        advancedOpen={advancedOpen}
        setAdvancedOpen={setAdvancedOpen}
        onSearch={() => {
          search()
          setPage(0)
        }}
      />

      <div className="list-table-container gsa-table-container">
        {isWideScreen ? (
          // ── Single row on wide screens (≥1536px) ──────────────────────────
          <div className="list-table-pagination-header">
            <div className="list-table-pagination-header-left">
              <Typography variant="h6">GSA MAS Contract Catalog</Typography>
              <div className="gsa-normalized-toggle">
                <Switch size="small" checked={normalizedMode} onChange={e => setNormalizedMode(e.target.checked)} />
                <Typography variant="body2" color={normalizedMode ? 'primary' : 'text.secondary'}>
                  IMPRES Normalized Fields
                </Typography>
              </div>
              {!normalizedMode && (
                <>
                  <TextField
                    select
                    size="small"
                    label="Display Template"
                    value={displayTemplateName}
                    onChange={e => handleSelectTemplate(e.target.value)}
                    className="gsa-template-select"
                  >
                    <MenuItem value="Default">Default</MenuItem>
                    {savedTemplates.map(t => {
                      const name = t.TemplateName.replace('gsa-mas:', '')
                      return (
                        <MenuItem key={t.TemplateName} value={name}>
                          {name}
                        </MenuItem>
                      )
                    })}
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={handleDeleteTemplate}
                    disabled={displayTemplateName === 'Default'}
                  >
                    Delete Template
                  </Button>
                  <Button variant="outlined" onClick={() => setEditDrawerOpen(true)}>
                    Edit Display View
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSaveViewOpen(true)
                      setViewName('')
                    }}
                  >
                    Save View
                  </Button>
                </>
              )}
            </div>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page:"
            />
          </div>
        ) : (
          // ── Two rows on laptops (<1536px) ─────────────────────────────────
          <div className="list-table-pagination-header gsa-toolbar-narrow">
            <div className="gsa-toolbar-row">
              <Typography variant="h6">GSA MAS Contract Catalog</Typography>
              <div className="gsa-normalized-toggle">
                <Switch size="small" checked={normalizedMode} onChange={e => setNormalizedMode(e.target.checked)} />
                <Typography variant="body2" color={normalizedMode ? 'primary' : 'text.secondary'}>
                  IMPRES Normalized Fields
                </Typography>
              </div>
            </div>
            {!normalizedMode && (
              <div className="gsa-toolbar-row-split">
                <div className="gsa-toolbar-buttons">
                  <TextField
                    select
                    size="small"
                    label="Display Template"
                    value={displayTemplateName}
                    onChange={e => handleSelectTemplate(e.target.value)}
                    className="gsa-template-select"
                  >
                    <MenuItem value="Default">Default</MenuItem>
                    {savedTemplates.map(t => {
                      const name = t.TemplateName.replace('gsa-mas:', '')
                      return (
                        <MenuItem key={t.TemplateName} value={name}>
                          {name}
                        </MenuItem>
                      )
                    })}
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={handleDeleteTemplate}
                    disabled={displayTemplateName === 'Default'}
                  >
                    Delete Template
                  </Button>
                  <Button variant="outlined" onClick={() => setEditDrawerOpen(true)}>
                    Edit Display View
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setSaveViewOpen(true)
                      setViewName('')
                    }}
                  >
                    Save View
                  </Button>
                </div>
                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={e => {
                    setRowsPerPage(Number(e.target.value))
                    setPage(0)
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Rows per page:"
                />
              </div>
            )}
          </div>
        )}

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {activeColumns.map(col => (
                  <TableCell key={col.key}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={activeColumns.length} className="gsa-status-cell">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={activeColumns.length} className="gsa-status-cell">
                    No records found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow key={row.id} hover className={expandedDesc[row.id] ? 'row-expanded' : undefined}>
                    {activeColumns.map(col => {
                      const value = row[col.key] ?? ''
                      if (col.key === 'productDescription') {
                        const expanded = expandedDesc[row.id]
                        const text = String(value)
                        const truncated = text.length > 120 && !expanded
                        return (
                          <TableCell key={col.key} className="gsa-desc-cell">
                            <Typography variant="body2">{truncated ? text.slice(0, 120) + '...' : text}</Typography>
                            {text.length > 120 && (
                              <Button
                                size="small"
                                onClick={() => setExpandedDesc(prev => ({ ...prev, [row.id]: !expanded }))}
                                className="gsa-desc-toggle"
                              >
                                {expanded ? 'SEE LESS' : 'SEE MORE'}
                              </Button>
                            )}
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell key={col.key}>
                          <Typography variant="body2">{String(value)}</Typography>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <EditDisplayFieldsDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        visibleKeys={visibleKeys}
        onSave={saveTemplate}
      />

      {saveViewOpen && (
        <div className="gsa-save-view-overlay" onClick={() => setSaveViewOpen(false)}>
          <Paper className="gsa-save-view-paper" onClick={e => e.stopPropagation()}>
            <Typography variant="h6" mb={1}>
              Save View
            </Typography>
            <Typography variant="body2" mb={1}>
              Enter a view name:
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={viewName}
              onChange={e => setViewName(e.target.value)}
            />
            <div className="gsa-save-view-actions">
              <Button variant="outlined" onClick={() => setSaveViewOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!viewName.trim()}
                onClick={() => {
                  saveTemplate(visibleKeys)
                  setSaveViewOpen(false)
                }}
              >
                Save View
              </Button>
            </div>
          </Paper>
        </div>
      )}
    </div>
  )
}
